import type { CareRequestModelView, CareRequestSubjectView } from '@actschurch/ccms-sdk';
import { cleanText } from './rules';
import {
  buildPerson, customDob, emptyPerson, type Candidates, type PersonResult, type RecordResult,
} from './people';

/** What we read from `careCaseService.getView(caseID).members[]`. */
export interface CaseMemberLike {
  memberID: number;
  fullName?: string | null;
  email?: string | null;
  phone?: string | null;
  dateOfBirth?: string | null;
  /** The member view may not carry gender; read defensively. */
  gender?: string | null;
  isRequester?: boolean;
  isMain?: boolean;
}

/** What we read from `careCaseService.getView(caseID)`. */
export interface CaseViewLike {
  members?: CaseMemberLike[] | null;
  /** The request this case was created from. Absent / null for a hand-created case. */
  requestID?: number | null;
  locationName?: string | null;
}

/** Source 2 for a case: the case view, plus its request when it has one. */
export interface CaseDetail {
  view: CaseViewLike;
  request: CareRequestModelView | null;
}

const norm = (v: unknown) => String(v ?? '').toLowerCase().replace(/\s+/g, ' ').trim();

/**
 * "Mary Ann Smith" → ["Mary Ann", "Smith"].
 * ⚠️ Lossy ("Jan van der Merwe" splits wrongly), so it is only the LAST choice for a name — the
 * request's own first name / surname come first whenever the person can be matched to one.
 */
export function splitName(full: string | null | undefined): [string | null, string | null] {
  const t = (full ?? '').trim().replace(/\s+/g, ' ');
  const cut = t.lastIndexOf(' ');
  return cut > 0 ? [t.slice(0, cut), t.slice(cut + 1)] : [t || null, null];
}

/** Which request subject is this case member? By member id, then exact name, then "the only one". */
function subjectFor(
  m: CaseMemberLike, request: CareRequestModelView | null, counseleeCount: number,
): CareRequestSubjectView | null {
  const subs = request?.subjects ?? [];
  if (subs.length === 0) return null;
  const name = norm(m.fullName);
  return (
    subs.find((s) => s.memberID != null && s.memberID === m.memberID)
    ?? (name !== '' ? subs.find((s) => norm(`${s.firstName ?? ''} ${s.surname ?? ''}`) === name) : undefined)
    ?? (subs.length === 1 && counseleeCount === 1 ? subs[0] : null)
  ) ?? null;
}

/**
 * Who needs care on this case, and where each of their values lives.
 *
 * Case members are always real members, so every person has a `memberID` and Source 3 can use the
 * safe by-id route.
 *
 *  • Counselees = the members, minus the requester when the request was made on someone else's behalf
 *    (the requester is then only the contact). Hand-created cases have no requester, so nobody is cut.
 *  • A = the member marked main (else the first), B = the other counselee.
 *  • Order per field: the member record on the case → the matching request subject → the requester's
 *    own details (only for the member who is the requester, on a self request).
 *  • The request's custom `date_of_birth` is used only when exactly one person needs care.
 *
 * ⚠️ Location is the CASE's. The request's location can differ (the sample request says Kinshasa for a
 * case opened in Cape Town), so the request's is never used.
 */
export function resolveCase(
  detail: CaseDetail,
  ctx: { id: number; reference: string; listLocation: string | null },
): RecordResult {
  const { view, request } = detail;
  const members = view.members ?? [];
  const onBehalf = request?.isForSelf === false;

  const counselees = members.filter((m) => !(onBehalf && m.isRequester && !m.isMain));
  const main = counselees.find((m) => m.isMain) ?? counselees[0] ?? null;
  const other = main ? counselees.find((m) => m !== main) ?? null : null;
  const single = counselees.length === 1;

  const personFor = (m: CaseMemberLike): PersonResult => {
    const subject = subjectFor(m, request, counselees.length);
    const requesterApplies = Boolean(
      request && !onBehalf && (m.isRequester || request.matchedMemberID === m.memberID),
    );
    const req = requesterApplies && request ? request : null;
    const [first, last] = splitName(m.fullName);

    const cand: Candidates = {
      firstName: [subject?.firstName, first, req?.firstName],
      surname: [subject?.surname, last, req?.surname],
      email: [m.email, subject?.email, req?.email],
      phone: [m.phone, subject?.phone, req?.phone],
      gender: [m.gender, subject?.gender, req?.gender],
      dateOfBirth: [m.dateOfBirth, subject?.dateOfBirth, ...(single && request ? [customDob(request)] : [])],
    };
    return buildPerson(cand, m.memberID ?? null);
  };

  return {
    id: ctx.id,
    reference: ctx.reference,
    location: cleanText(ctx.listLocation) ?? cleanText(view.locationName),
    a: main ? personFor(main) : emptyPerson(),
    b: other ? personFor(other) : null,
    extraPeople: Math.max(0, counselees.length - 2),
    failed: false,
    requireLocation: true,
  };
}
